import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, ShieldCheck, Phone, Zap } from "lucide-react";
import IntakeForm from "@/components/IntakeForm";
import BookConsult from "@/components/BookConsult";
import { PRACTICE_AREAS, STEPS, EXAMPLES } from "@/lib/intakeData";

const scrollToIntake = () => {
  const el = document.getElementById("intake");
  if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
};

export default function Landing() {
  const [showConsult, setShowConsult] = useState(false);
  const [showExitModal, setShowExitModal] = useState(false);
  const [exitFired, setExitFired] = useState(false);

  // Exit-intent: trigger when cursor leaves viewport top, once per session
  useEffect(() => {
    if (sessionStorage.getItem("tfm_exit_seen")) { setExitFired(true); return; }
    const onLeave = (e) => {
      if (exitFired) return;
      if (e.clientY <= 0) {
        setExitFired(true);
        sessionStorage.setItem("tfm_exit_seen", "1");
        setShowExitModal(true);
      }
    };
    document.addEventListener("mouseout", onLeave);
    return () => document.removeEventListener("mouseout", onLeave);
  }, [exitFired]);

  return (
    <div data-testid="landing-page">
      {/* Announcement */}
      <div className="ann-bar" data-testid="announcement-bar">
        Attorney advertisement · Prefcap Law (Houston, Texas) &nbsp;·&nbsp;
        <a onClick={(e) => { e.preventDefault(); scrollToIntake(); }} href="#intake">Begin your assessment →</a>
      </div>

      {/* Nav */}
      <nav className="nav" data-testid="main-nav">
        <div className="container-tx nav-inner">
          <div className="brand" onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })} data-testid="brand-logo">
            <div className="brand-mark">tx</div>
            <div>
              <div className="brand-name">TexasBusinessDispute.com</div>
              <div className="brand-sub">Prefcap Law · Houston, Texas</div>
            </div>
          </div>
          <div className="nav-links">
            <a className="nav-link" href="#how" data-testid="nav-how">Process</a>
            <a className="nav-link" href="#practice" data-testid="nav-practice">Practice</a>
            <a className="nav-link" href="#examples" data-testid="nav-examples">Examples</a>
            <Link className="nav-link" to="/blog" data-testid="nav-blog">Insights</Link>
          </div>
          <div className="nav-actions">
            <a className="nav-phone" href="tel:+17132912875" data-testid="nav-phone-cta">
              <Phone size={13} /> (713) 291-2875
            </a>
            <button className="nav-cta" onClick={scrollToIntake} data-testid="nav-cta-btn">
              Free Case Assessment <ArrowRight size={14} />
            </button>
          </div>
        </div>
      </nav>

      {/* Hero */}
      <section className="hero" id="top" data-testid="hero-section">
        <div className="container-tx">
          <div className="hero-layout">
            <div>
              <div className="hero-tag fadeup fu1"><div className="hero-tag-line" />Texas Business Litigation Counsel</div>
              <h1 className="hero-h fadeup fu2 serif">
                Your dispute.<br />
                <em>The right firm.</em><br />
                Every matter.
              </h1>
              <p className="hero-body fadeup fu3">When your business is in a legal dispute — a contract gone wrong, a partner acting in bad faith, a competitor crossing the line — choosing the right litigation firm is the most important decision you'll make. We assess your matter and connect you with a Texas firm whose practice, experience, and approach are well suited to your case.</p>
              <div className="trust-row fadeup fu4">
                <div className="trust-item"><div className="trust-check">✓</div>Confidential intake — your information stays private</div>
                <div className="trust-item"><div className="trust-check">✓</div>Connected to Texas-licensed attorneys by practice area and jurisdiction</div>
                <div className="trust-item"><div className="trust-check">✓</div>Securities, energy, contracts, corporate governance, real estate</div>
                <div className="trust-item"><div className="trust-check">✓</div>No obligation to retain — assessment is free</div>
              </div>

              {/* Urgent paid consult CTA */}
              <div className="urgent-cta fadeup fu5" data-testid="urgent-cta">
                <div className="urgent-cta-icon"><Zap size={14} /></div>
                <div style={{ flex: 1 }}>
                  <div className="urgent-cta-title">Time-sensitive matter?</div>
                  <div className="urgent-cta-sub">Skip the form. Book a 15-minute paid consultation with Reza directly — same-day availability.</div>
                </div>
                <button className="btn-ghost urgent-cta-btn" onClick={() => setShowConsult(true)} data-testid="urgent-cta-btn">
                  Book paid consult · $95
                </button>
              </div>
            </div>
            <IntakeForm />
          </div>
        </div>
      </section>

      {/* Social proof strip */}
      <div className="social-proof" data-testid="social-proof">
        <div className="container-tx social-proof-inner">
          <span className="sp-label">Areas we serve</span>
          <span className="sp-city">Houston</span>
          <span className="sp-dot">·</span>
          <span className="sp-city">Dallas</span>
          <span className="sp-dot">·</span>
          <span className="sp-city">Austin</span>
          <span className="sp-dot">·</span>
          <span className="sp-city">Fort Worth</span>
          <span className="sp-dot">·</span>
          <span className="sp-city">San Antonio</span>
          <span className="sp-dot">·</span>
          <span className="sp-city">Midland · Permian</span>
          <span className="sp-dot">·</span>
          <span className="sp-city">Tyler · East Texas</span>
        </div>
      </div>

      {/* How */}
      <section className="sec how grain" id="how" data-testid="how-section">
        <div className="container-tx">
          <div className="how-grid">
            <div>
              <div className="eyebrow white">The process</div>
              <h2>Assessed, matched, and represented — <em>without the guesswork.</em></h2>
              <p className="lead">Choosing a litigation firm without guidance is like diagnosing yourself before seeing a specialist. The right firm for a securities fraud case in the Southern District of Texas is not the same as the right firm for an energy JOA dispute in the Permian Basin. We know the difference.</p>
              <div className="how-card">
                <div className="label">Why firm matching matters</div>
                <p>A general commercial litigator and a firm with a dedicated energy practice have fundamentally different value propositions for a royalty dispute. A firm with deep SEC connections handles securities fraud differently than one without.</p>
              </div>
            </div>
            <div className="steps">
              {STEPS.map((s, i) => (
                <div className="step fadeup" key={s.n} style={{ animationDelay: `${i * 0.08}s` }}>
                  <div className="step-n">{s.n}</div>
                  <div>
                    <h3>{s.title}</h3>
                    <p>{s.body}</p>
                    <span className="step-tag">{s.tag}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Practice areas */}
      <section className="sec practice" id="practice" data-testid="practice-section">
        <div className="container-tx">
          <div className="section-intro">
            <div className="eyebrow copper">Practice areas</div>
            <h2>The disputes <em>we assess and match</em></h2>
            <p className="lead">Our network spans every major category of Texas business litigation — with matter-specific experience in each area, not generalist referrals.</p>
          </div>
          <div className="areas-grid">
            {PRACTICE_AREAS.map((a) => (
              <div className="area" key={a.title} data-testid={`practice-area-${a.title.replace(/\W+/g, "-").toLowerCase()}`}>
                <h3 className="serif">{a.title}</h3>
                <p>{a.body}</p>
                {a.stat ? <div className="area-stat">{a.stat}</div> : null}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Examples */}
      <section className="sec examples" id="examples" data-testid="examples-section">
        <div className="container-tx">
          <div className="eyebrow copper">Case examples</div>
          <h2>Matters we <em>assess and refer</em></h2>
          <p className="lead" style={{ marginTop: 10 }}>These examples illustrate the types of Texas business disputes appropriate for firm referral through this network. They are illustrative only.</p>
          <div className="ex-grid">
            {EXAMPLES.map((e, i) => (
              <div className="ex" key={i} data-testid={`example-${i}`}>
                <div className="ex-type">{e.type}</div>
                <p className="ex-scenario">"{e.scenario}"</p>
                <div className="ex-checks">
                  {e.checks.map((c, ci) => <div className="ex-ck" key={ci}>✓ {c}</div>)}
                </div>
                <span className="ex-verdict">Assessed — Firm Matched</span>
              </div>
            ))}
          </div>
          <p className="ex-disc">These are illustrative examples only and do not represent actual clients or matters handled through this network. Whether a specific matter is accepted for referral is determined by the receiving firm. No outcome is guaranteed.</p>
        </div>
      </section>

      {/* Bottom CTA */}
      <section className="bottom-cta" data-testid="bottom-cta">
        <div className="container-tx">
          <h2 className="serif">Your dispute deserves<br /><em>the right firm.</em></h2>
          <p>Confidential assessment. No obligation. Connected to a Texas firm whose practice may fit your case.</p>
          <div style={{ display: "flex", gap: 12, justifyContent: "center", flexWrap: "wrap" }}>
            <button className="nav-cta" style={{ fontSize: 14, padding: "14px 32px" }} onClick={scrollToIntake} data-testid="bottom-cta-btn">
              Begin Free Assessment <ArrowRight size={14} />
            </button>
            <button className="btn-ghost" style={{ fontSize: 13, padding: "13px 24px", background: "transparent", color: "#fff", borderColor: "rgba(255,255,255,0.4)" }} onClick={() => setShowConsult(true)} data-testid="bottom-consult-btn">
              <Zap size={13} /> Book paid consult — $95
            </button>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="footer" data-testid="footer">
        <div className="container-tx">
          <div className="footer-grid">
            <div>
              <div className="brand" style={{ marginBottom: 14 }}>
                <div className="brand-mark">tx</div>
                <div>
                  <div className="brand-name" style={{ color: "#fbf8f3" }}>TexasBusinessDispute.com</div>
                  <div className="brand-sub">Prefcap Law · Houston, Texas</div>
                </div>
              </div>
              <p>Texas attorney-operated business litigation intake and firm matching service. Confidential intake. Texas-licensed firm referral. No obligation.</p>
              <p style={{ marginTop: 14, display: "flex", alignItems: "center", gap: 8 }}>
                <ShieldCheck size={14} style={{ color: "var(--copper-soft)" }} /> Confidential · No obligation
              </p>
            </div>
            <div>
              <h4>Jurisdictions</h4>
              <ul>
                <li>S.D. Texas — Houston</li>
                <li>N.D. Texas — Dallas / Fort Worth</li>
                <li>W.D. Texas — Austin / San Antonio / Midland</li>
                <li>E.D. Texas — Tyler / Beaumont</li>
                <li>Texas state courts statewide</li>
              </ul>
            </div>
            <div>
              <h4>Quick Links</h4>
              <ul>
                <li><a style={{ color: "inherit", textDecoration: "none" }} href="#how">How it works</a></li>
                <li><a style={{ color: "inherit", textDecoration: "none" }} href="#practice">Practice areas</a></li>
                <li><a style={{ color: "inherit", textDecoration: "none" }} href="#examples">Examples</a></li>
                <li><Link style={{ color: "inherit", textDecoration: "none" }} to="/blog" data-testid="footer-blog-link">Insights</Link></li>
                <li><Link style={{ color: "inherit", textDecoration: "none" }} to="/sms-opt-in" data-testid="footer-sms-link">SMS Policy</Link></li>
                <li><Link style={{ color: "inherit", textDecoration: "none" }} to="/admin/login" data-testid="footer-admin-link">Admin</Link></li>
              </ul>
            </div>
          </div>
          <div className="footer-bottom">
            <p style={{ marginBottom: 12 }}>
              Referral fee disclosure. TexasBusinessDispute.com is a trade name of Prefcap Law (principal office: Houston, Texas), operated by lead attorney Reza Chawla. If a referral or any other fee is paid, in accordance with the Texas Disciplinary Rules of Professional Conduct (including Rule 1.04(f)), the client will be informed in writing of (i) the identities of the lawyers and firms involved, (ii) the division of fees between them, and (iii) the responsibility each lawyer is assuming. All such terms are documented in the client engagement letter for the matter, and the client's written consent will be obtained before any referral is finalized and before any fee is shared.
            </p>
            © {new Date().getFullYear()} TexasBusinessDispute.com · Prefcap Law (Houston, Texas). Attorney advertisement. This site provides a confidential intake and Texas-licensed firm referral. It does not create an attorney-client relationship; information you submit before an engagement letter is signed is treated as confidential but is not protected by the attorney-client privilege. Past results do not guarantee a similar outcome.
          </div>
        </div>
      </footer>

      {/* Paid consult booking modal */}
      <BookConsult open={showConsult} onClose={() => setShowConsult(false)} />

      {/* Exit-intent modal */}
      {showExitModal && (
        <>
          <div className="drawer-bg" onClick={() => setShowExitModal(false)} data-testid="exit-modal-bg" />
          <div className="exit-modal" data-testid="exit-modal">
            <button className="consult-close" onClick={() => setShowExitModal(false)} data-testid="exit-modal-close">×</button>
            <div className="intake-eyebrow">Before you go</div>
            <h3 className="serif" style={{ fontSize: 24, fontWeight: 600, margin: "8px 0 10px", letterSpacing: "-0.02em" }}>Your matter has a clock.</h3>
            <p style={{ fontSize: 13.5, color: "var(--muted)", lineHeight: 1.7, marginBottom: 16 }}>
              Texas filing deadlines for business claims can run from the underlying conduct or from when you discovered the harm — and missing one permanently bars the claim. The free assessment takes three minutes.
            </p>
            <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
              <button className="btn-copper" onClick={() => { setShowExitModal(false); scrollToIntake(); }} data-testid="exit-modal-assess-btn" style={{ flex: 1 }}>
                Begin free assessment →
              </button>
              <button className="btn-ghost" onClick={() => { setShowExitModal(false); setShowConsult(true); }} data-testid="exit-modal-consult-btn" style={{ flex: 1 }}>
                <Zap size={13} /> Book paid consult
              </button>
            </div>
            <a href="tel:+17132912875" style={{ display: "block", textAlign: "center", marginTop: 16, fontFamily: "var(--mono)", fontSize: 11, letterSpacing: ".12em", color: "var(--muted)", textDecoration: "none" }} data-testid="exit-modal-phone">
              OR CALL DIRECTLY: (713) 291-2875
            </a>
          </div>
        </>
      )}
    </div>
  );
}
